"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useCallback, useRef, useState, useEffect, type ReactElement, memo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { T } from "gt-next";
import { Table } from "@/types";
import { classNames } from "@/lib/utils";
import { useModalContext } from "@/contexts/ModalContextProvider";
import { useLoggedInUser } from "@/hooks/useLoggedInUser";
import { useLocalizedAppPath } from "@/contexts/AppLocaleContext";
import { DEMO_USER_EMAIL } from "@/constants";
import { AnimatedIcon, type AnimationType, type AnimatedIconHandle } from "@/components/ui/animated-icon";
import { TrendingUpIcon, type TrendingUpIconHandle } from "@/components/ui/trending-up";
import { ChartColumnIncreasingIcon, type ChartColumnIncreasingIconHandle } from "@/components/ui/chart-column-increasing";
import { CalendarDaysIcon, type CalendarDaysIconHandle } from "@/components/ui/calendar-days";
import { ZapIcon, type ZapIconHandle } from "@/components/ui/zap";
import { BadgeCheckIcon, type BadgeCheckIconHandle } from "@/components/ui/badge-check";

// Unified icon handle type for all custom animated icons
type CustomIconHandle = TrendingUpIconHandle | ChartColumnIncreasingIconHandle | CalendarDaysIconHandle | ZapIconHandle | BadgeCheckIconHandle;

// Lucide Icons (for simpler icons that use AnimatedIcon wrapper)
import { LayoutGrid, Gift, Plus, ChevronDown, Users, HelpCircle, Phone } from "lucide-react";
import { BRAND } from "@/lib/branding";
import SidebarAccountSelector, { getStoredSelectedAccountId } from "./SidebarAccountSelector";
import SetupChecklist from "./SetupChecklist";

// ============================================
// PATH MATCHING UTILITIES
// ============================================

/**
 * Hook to determine if a path is active
 * Called only by leaf components to isolate pathname subscriptions
 */
function useIsPathActive(href, t0) {
  const $ = _c(4);
  const exactMatch = t0 === undefined ? false : t0;
  const pathname = usePathname();
  if (!href) {
    return false;
  }
  let t1;
  if ($[0] !== exactMatch || $[1] !== href || $[2] !== pathname) {
    t1 = exactMatch ? pathname === href : pathname.includes(href);
    $[0] = exactMatch;
    $[1] = href;
    $[2] = pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// ============================================
// SIDEBAR EXPAND STATE PERSISTENCE
// ============================================
const GROWTH_EXPANDED_KEY = "sidebar-growth-expanded";
const CONTENT_EXPANDED_KEY = "sidebar-content-expanded";
function getStoredExpandedState(): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(GROWTH_EXPANDED_KEY);
    return stored !== null ? stored === "true" : null;
  } catch {
    return null;
  }
}
function setStoredExpandedState(expanded: boolean): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(GROWTH_EXPANDED_KEY, String(expanded));
  } catch {
    // localStorage not available
  }
}
function getStoredContentExpandedState(): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(CONTENT_EXPANDED_KEY);
    return stored !== null ? stored === "true" : null;
  } catch {
    return null;
  }
}
function setStoredContentExpandedState(expanded: boolean): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(CONTENT_EXPANDED_KEY, String(expanded));
  } catch {
    // localStorage not available
  }
}
interface AppSidebarProps {
  userProfile: Table<"user_profiles">;
  hasUpgradableAccounts: boolean;
}

// Contextual badge component for menu items
function MenuBadge(t0) {
  const $ = _c(5);
  const {
    type
  } = t0;
  let t1;
  if ($[0] !== type) {
    const styles = {
      AI: "bg-black text-white shadow-sm border border-black/10",
      PRO: "bg-gradient-to-r from-amber-200 to-yellow-400 text-yellow-900 border border-yellow-400/20 shadow-sm",
      BETA: "bg-white text-gray-500",
      NEW: "bg-black text-white shadow-sm border border-black/10",
      SOON: "bg-white text-gray-500"
    };
    t1 = classNames("flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-bold leading-none tracking-wide", styles[type]);
    $[0] = type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== type) {
    t2 = <span className={t1}>{type}</span>;
    $[2] = t1;
    $[3] = type;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// Section header for grouping nav items
function SectionHeader(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="px-2.5 pb-1.5 pt-3 text-xs font-medium uppercase tracking-wide text-black/40">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
interface SidebarLinkProps {
  href?: string;
  icon?: ReactElement;
  animation?: AnimationType;
  /** Render prop for custom animated icon - receives className for color styling */
  renderCustomIcon?: (className: string) => React.ReactNode;
  /** Ref for custom icon's imperative handle */
  customIconRef?: React.RefObject<CustomIconHandle | null>;
  children: React.ReactNode;
  /** Explicit active state - if not provided, computed from href using pathname */
  active?: boolean;
  /** Use exact path matching instead of includes (only when active is not provided) */
  exactMatch?: boolean;
  onClick?: () => void;
  badge?: React.ReactNode;
}

/**
 * Call Support: opens the dialer on phones. On a computer a tel: link does nothing unless a
 * calling app is set up, so the click also shows the number (copied to the clipboard) to dial.
 */
function callSupport() {
  const telHref = `tel:${BRAND.supportPhone.replace(/[^+\d]/g, "")}`;
  window.location.href = telHref;
  if (window.matchMedia("(pointer: coarse)").matches) return;
  navigator.clipboard?.writeText(BRAND.supportPhone).catch(() => {});
  toast(`Call support: ${BRAND.supportPhone}`, {
    description: "Number copied to your clipboard."
  });
}

/**
 * SidebarLink - Navigation link with optional self-managed active state
 * When `active` prop is omitted, the component uses usePathname internally
 * to determine its own active state, isolating pathname subscriptions to leaf components
 */
const SidebarLink = memo(function SidebarLink(t0) {
  const $ = _c(39);
  const {
    href,
    icon,
    animation: t1,
    renderCustomIcon,
    customIconRef,
    children,
    active: activeProp,
    exactMatch: t2,
    onClick,
    badge
  } = t0;
  const animation = t1 === undefined ? "scale" : t1;
  const exactMatch = t2 === undefined ? false : t2;
  const router = useRouter();
  const iconRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const internalActive = useIsPathActive(activeProp === undefined ? href : undefined, exactMatch);
  const active = activeProp ?? internalActive;
  let t3;
  let t4;
  if ($[0] !== active || $[1] !== animation || $[2] !== badge || $[3] !== children || $[4] !== customIconRef || $[5] !== href || $[6] !== icon || $[7] !== isHovered || $[8] !== onClick || $[9] !== renderCustomIcon || $[10] !== router) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const baseClasses = classNames("group relative flex items-center gap-2.5 overflow-hidden rounded-xl px-2.5 py-2.5 text-base font-medium min-h-[44px]", "transition-colors duration-150 ease-out");
      let t5;
      if ($[13] !== customIconRef || $[14] !== href || $[15] !== onClick || $[16] !== router) {
        t5 = () => {
          setIsHovered(true);
          if (href && !onClick) {
            router.prefetch(href);
          }
          iconRef.current?.startAnimation();
          customIconRef?.current?.startAnimation();
        };
        $[13] = customIconRef;
        $[14] = href;
        $[15] = onClick;
        $[16] = router;
        $[17] = t5;
      } else {
        t5 = $[17];
      }
      const handleMouseEnter = t5;
      let t6;
      if ($[18] !== customIconRef) {
        t6 = () => {
          setIsHovered(false);
          iconRef.current?.stopAnimation();
          customIconRef?.current?.stopAnimation();
        };
        $[18] = customIconRef;
        $[19] = t6;
      } else {
        t6 = $[19];
      }
      const handleMouseLeave = t6;
      const t7 = active ? "rgba(255,255,255,0.85)" : isHovered ? "rgba(255,255,255,0.6)" : "transparent";
      const t8 = active || isHovered ? "#141414" : "rgba(20,20,20,0.65)";
      let t9;
      if ($[20] !== t7 || $[21] !== t8) {
        t9 = {
          backgroundColor: t7,
          color: t8
        };
        $[20] = t7;
        $[21] = t8;
        $[22] = t9;
      } else {
        t9 = $[22];
      }
      const dynamicStyle = t9;
      let t10;
      let t11;
      if ($[23] !== active || $[24] !== animation || $[25] !== icon || $[26] !== isHovered || $[27] !== renderCustomIcon) {
        const iconColorClass = classNames("transition-colors duration-150", active || isHovered ? "text-[#141414]" : "text-[rgba(20,20,20,0.45)]");
        if ($[30] !== active) {
          t10 = active && <span className="absolute inset-y-0 -left-[2px] my-auto h-5 w-[5px] rounded-full bg-[#141414]" />;
          $[30] = active;
          $[31] = t10;
        } else {
          t10 = $[31];
        }
        t11 = renderCustomIcon ? renderCustomIcon(iconColorClass) : icon ? <AnimatedIcon ref={iconRef} icon={icon} animation={animation} animateOnHover={false} size={20} className={iconColorClass} /> : null;
        $[23] = active;
        $[24] = animation;
        $[25] = icon;
        $[26] = isHovered;
        $[27] = renderCustomIcon;
        $[28] = t10;
        $[29] = t11;
      } else {
        t10 = $[28];
        t11 = $[29];
      }
      let t12;
      if ($[32] !== children) {
        t12 = <span className="flex-1">{children}</span>;
        $[32] = children;
        $[33] = t12;
      } else {
        t12 = $[33];
      }
      let t13;
      if ($[34] !== badge || $[35] !== t10 || $[36] !== t11 || $[37] !== t12) {
        t13 = <>{t10}{t11}{t12}{badge}</>;
        $[34] = badge;
        $[35] = t10;
        $[36] = t11;
        $[37] = t12;
        $[38] = t13;
      } else {
        t13 = $[38];
      }
      const content = t13;
      if (onClick) {
        t4 = <button type="button" onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={classNames(baseClasses, active && "shadow-sm backdrop-blur-sm", "w-full text-left")} style={dynamicStyle}>{content}</button>;
        break bb0;
      }
      t3 = <Link href={href || "#"} className={classNames(baseClasses, active && "shadow-sm backdrop-blur-sm")} style={dynamicStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>{content}</Link>;
    }
    $[0] = active;
    $[1] = animation;
    $[2] = badge;
    $[3] = children;
    $[4] = customIconRef;
    $[5] = href;
    $[6] = icon;
    $[7] = isHovered;
    $[8] = onClick;
    $[9] = renderCustomIcon;
    $[10] = router;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  return t3;
});

// Expandable sidebar item with Linktree-style children
interface SidebarExpandableItemProps {
  icon?: ReactElement;
  animation?: AnimationType;
  /** Render prop for custom animated icon - receives className for color styling */
  renderCustomIcon?: (className: string) => React.ReactNode;
  /** Ref for custom icon's imperative handle */
  customIconRef?: React.RefObject<TrendingUpIconHandle | null>;
  label: React.ReactNode;
  children: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  hasActiveChild?: boolean;
}
function SidebarExpandableItem(t0) {
  const $ = _c(54);
  const {
    icon,
    animation: t1,
    renderCustomIcon,
    customIconRef,
    label,
    children,
    isExpanded,
    onToggle,
    hasActiveChild: t2
  } = t0;
  const animation = t1 === undefined ? "bounce" : t1;
  const hasActiveChild = t2 === undefined ? false : t2;
  const iconRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  let dynamicStyle;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== customIconRef || $[1] !== hasActiveChild || $[2] !== isExpanded || $[3] !== isHovered || $[4] !== onToggle) {
    const baseClasses = classNames("group relative flex items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-base font-medium", "w-full cursor-pointer text-left transition-colors duration-150 ease-out");
    let t9;
    if ($[12] !== customIconRef) {
      t9 = () => {
        setIsHovered(true);
        iconRef.current?.startAnimation();
        customIconRef?.current?.startAnimation();
      };
      $[12] = customIconRef;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    const handleMouseEnter = t9;
    let t10;
    if ($[14] !== customIconRef) {
      t10 = () => {
        setIsHovered(false);
        iconRef.current?.stopAnimation();
        customIconRef?.current?.stopAnimation();
      };
      $[14] = customIconRef;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    const handleMouseLeave = t10;
    const t11 = hasActiveChild ? "rgba(255,255,255,0.85)" : isHovered ? "rgba(255,255,255,0.6)" : "transparent";
    const t12 = hasActiveChild || isHovered ? "#141414" : "rgba(20,20,20,0.65)";
    let t13;
    if ($[16] !== t11 || $[17] !== t12) {
      t13 = {
        backgroundColor: t11,
        color: t12
      };
      $[16] = t11;
      $[17] = t12;
      $[18] = t13;
    } else {
      t13 = $[18];
    }
    dynamicStyle = t13;
    t3 = "button";
    t4 = onToggle;
    t5 = handleMouseEnter;
    t6 = handleMouseLeave;
    t7 = isExpanded;
    t8 = classNames(baseClasses, hasActiveChild && "shadow-sm backdrop-blur-sm");
    $[0] = customIconRef;
    $[1] = hasActiveChild;
    $[2] = isExpanded;
    $[3] = isHovered;
    $[4] = onToggle;
    $[5] = dynamicStyle;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    dynamicStyle = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
  }
  let t9;
  if ($[19] !== animation || $[20] !== hasActiveChild || $[21] !== icon || $[22] !== isHovered || $[23] !== renderCustomIcon) {
    t9 = renderCustomIcon ? renderCustomIcon(classNames("transition-colors duration-150", hasActiveChild || isHovered ? "text-[#141414]" : "text-[rgba(20,20,20,0.45)]")) : icon ? <AnimatedIcon ref={iconRef} icon={icon} animation={animation} animateOnHover={false} size={20} className={classNames("transition-colors duration-150", hasActiveChild || isHovered ? "text-[#141414]" : "text-[rgba(20,20,20,0.45)]")} /> : null;
    $[19] = animation;
    $[20] = hasActiveChild;
    $[21] = icon;
    $[22] = isHovered;
    $[23] = renderCustomIcon;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== label) {
    t10 = <span className="flex-1">{label}</span>;
    $[25] = label;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const t11 = isExpanded ? "rotate-0" : "-rotate-90";
  const t12 = hasActiveChild || isHovered ? "text-[#141414]" : "text-[rgba(20,20,20,0.45)]";
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = classNames("h-4 w-4 transition-transform duration-200", t11, t12);
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <ChevronDown className={t13} />;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== dynamicStyle || $[33] !== t10 || $[34] !== t14 || $[35] !== t3 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6 || $[39] !== t7 || $[40] !== t8 || $[41] !== t9) {
    t15 = <button type={t3} onClick={t4} onMouseEnter={t5} onMouseLeave={t6} aria-expanded={t7} className={t8} style={dynamicStyle}>{t9}{t10}{t14}</button>;
    $[32] = dynamicStyle;
    $[33] = t10;
    $[34] = t14;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const t16 = isExpanded ? "max-h-[300px] opacity-100" : "max-h-0 opacity-0";
  let t17;
  if ($[43] !== t16) {
    t17 = classNames("overflow-hidden transition-[max-height,opacity] duration-200 ease-out", t16);
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="absolute bottom-3 left-0 top-2 w-[1.5px] rounded-full bg-black/10" />;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== children) {
    t19 = <div className="relative ml-5 space-y-1 py-1 pl-3.5">{t18}{children}</div>;
    $[46] = children;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t17 || $[49] !== t19) {
    t20 = <div className={t17}>{t19}</div>;
    $[48] = t17;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t15 || $[52] !== t20) {
    t21 = <div>{t15}{t20}</div>;
    $[51] = t15;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  return t21;
}

// Child link for expandable sidebar items - Linktree style
interface SidebarChildLinkProps {
  href: string;
  children: React.ReactNode;
  /** Explicit active state - if not provided, computed from href using pathname */
  active?: boolean;
  /** Use exact path matching instead of includes (only when active is not provided) */
  exactMatch?: boolean;
  badge?: React.ReactNode;
}

/**
 * SidebarChildLink - Child navigation link with optional self-managed active state
 * When `active` prop is omitted, uses usePathname internally
 */
const SidebarChildLink = memo(function SidebarChildLink(t0) {
  const $ = _c(21);
  const {
    href,
    children,
    active: activeProp,
    exactMatch: t1,
    badge
  } = t0;
  const exactMatch = t1 === undefined ? true : t1;
  const router = useRouter();
  const [isHovered, setIsHovered] = useState(false);
  const internalActive = useIsPathActive(activeProp === undefined ? href : undefined, exactMatch);
  const active = activeProp ?? internalActive;
  let t2;
  if ($[0] !== href || $[1] !== router) {
    t2 = () => {
      setIsHovered(true);
      router.prefetch(href);
    };
    $[0] = href;
    $[1] = router;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleMouseEnter = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsHovered(false);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleMouseLeave = t3;
  const t4 = active ? "rgba(0,0,0,0.04)" : isHovered ? "rgba(0,0,0,0.03)" : "transparent";
  const t5 = active ? "#000000" : isHovered ? "#141414" : "rgba(20,20,20,0.65)";
  let t6;
  if ($[4] !== t4 || $[5] !== t5) {
    t6 = {
      backgroundColor: t4,
      color: t5
    };
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const dynamicStyle = t6;
  const t7 = active ? "font-semibold" : "font-medium";
  let t8;
  if ($[7] !== t7) {
    t8 = classNames("relative flex items-center overflow-hidden rounded-lg px-2.5 py-2 text-[15px] min-h-[44px]", "transition-colors duration-150 ease-out", t7);
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== active) {
    t9 = active && <span className="absolute inset-y-0 -left-[2px] my-auto h-5 w-[5px] rounded-full bg-[#141414]" />;
    $[9] = active;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== children) {
    t10 = <span className="flex-1">{children}</span>;
    $[11] = children;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== badge || $[14] !== dynamicStyle || $[15] !== handleMouseEnter || $[16] !== href || $[17] !== t10 || $[18] !== t8 || $[19] !== t9) {
    t11 = <Link href={href} className={t8} style={dynamicStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>{t9}{t10}{badge}</Link>;
    $[13] = badge;
    $[14] = dynamicStyle;
    $[15] = handleMouseEnter;
    $[16] = href;
    $[17] = t10;
    $[18] = t8;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
});

// ============================================
// GROWTH NAV SECTION - Self-contained with pathname subscription
// ============================================

interface GrowthNavSectionProps {
  localizedPath: (path: string) => string;
}

/**
 * GrowthNavSection - Encapsulates all growth-related navigation with its own pathname subscription
 * This isolates pathname changes to just this section, preventing full sidebar re-renders
 */
const GrowthNavSection = memo(function GrowthNavSection(t0) {
  const $ = _c(68);
  const {
    localizedPath
  } = t0;
  const pathname = usePathname();
  const growthIconRef = useRef(null);
  let id;
  let t1;
  if ($[0] !== pathname) {
    id = pathname.split("/")[3];
    t1 = id && id.match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/);
    $[0] = pathname;
    $[1] = id;
    $[2] = t1;
  } else {
    id = $[1];
    t1 = $[2];
  }
  const isValidUUID = t1;
  const accountIdFromUrl = isValidUUID ? id : null;
  const [storedAccountId, setStoredAccountId] = useState(null);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const stored = getStoredSelectedAccountId();
      if (stored) {
        setStoredAccountId(stored);
      }
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const accountId = accountIdFromUrl || storedAccountId;
  let t4;
  if ($[5] !== pathname) {
    t4 = pathname?.includes("/app/account/") && !pathname?.includes("/schedule") && !pathname?.includes("/ai-content") && !pathname?.includes("/viral");
    $[5] = pathname;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const isOnGrowthPage = t4;
  const [isGrowthExpanded, setIsGrowthExpanded] = useState(isOnGrowthPage);
  let t5;
  if ($[7] !== isOnGrowthPage) {
    t5 = () => {
      if (!isOnGrowthPage) {
        const stored_0 = getStoredExpandedState();
        if (stored_0 !== null) {
          setIsGrowthExpanded(stored_0);
        }
      }
    };
    $[7] = isOnGrowthPage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] !== isGrowthExpanded || $[11] !== isOnGrowthPage) {
    t7 = () => {
      if (isOnGrowthPage && !isGrowthExpanded) {
        setIsGrowthExpanded(true);
      }
    };
    $[10] = isGrowthExpanded;
    $[11] = isOnGrowthPage;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== isOnGrowthPage) {
    t8 = [isOnGrowthPage];
    $[13] = isOnGrowthPage;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsGrowthExpanded(_temp);
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleGrowthToggle = t9;
  let t10;
  bb0: {
    if (!accountId) {
      let t11;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = [];
        $[16] = t11;
      } else {
        t11 = $[16];
      }
      t10 = t11;
      break bb0;
    }
    const t11 = `/app/account/${accountId}`;
    let t12;
    if ($[17] !== localizedPath || $[18] !== t11) {
      t12 = localizedPath(t11);
      $[17] = localizedPath;
      $[18] = t11;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    let t13;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <T>Overview</T>;
      $[20] = t13;
    } else {
      t13 = $[20];
    }
    let t14;
    if ($[21] !== t12) {
      t14 = {
        href: t12,
        title: t13,
        exactMatch: true
      };
      $[21] = t12;
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    const t15 = `/app/account/${accountId}/settings`;
    let t16;
    if ($[23] !== localizedPath || $[24] !== t15) {
      t16 = localizedPath(t15);
      $[23] = localizedPath;
      $[24] = t15;
      $[25] = t16;
    } else {
      t16 = $[25];
    }
    let t17;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <T>Targeting</T>;
      $[26] = t17;
    } else {
      t17 = $[26];
    }
    let t18;
    if ($[27] !== t16) {
      t18 = {
        href: t16,
        title: t17,
        exactMatch: true
      };
      $[27] = t16;
      $[28] = t18;
    } else {
      t18 = $[28];
    }
    const t19 = `/app/account/${accountId}/max`;
    let t20;
    if ($[29] !== localizedPath || $[30] !== t19) {
      t20 = localizedPath(t19);
      $[29] = localizedPath;
      $[30] = t19;
      $[31] = t20;
    } else {
      t20 = $[31];
    }
    let t21;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <span className="outfame-max-gradient">Max™</span>;
      $[32] = t21;
    } else {
      t21 = $[32];
    }
    let t22;
    if ($[33] !== t20) {
      t22 = {
        href: t20,
        title: t21,
        badge: "NEW" as const,
        exactMatch: false
      };
      $[33] = t20;
      $[34] = t22;
    } else {
      t22 = $[34];
    }
    const t23 = `/app/account/${accountId}/boost`;
    let t24;
    if ($[35] !== localizedPath || $[36] !== t23) {
      t24 = localizedPath(t23);
      $[35] = localizedPath;
      $[36] = t23;
      $[37] = t24;
    } else {
      t24 = $[37];
    }
    let t25;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <T>Boost Post</T>;
      $[38] = t25;
    } else {
      t25 = $[38];
    }
    let t26;
    if ($[39] !== t24) {
      t26 = {
        href: t24,
        title: t25,
        badge: "NEW" as const,
        exactMatch: false
      };
      $[39] = t24;
      $[40] = t26;
    } else {
      t26 = $[40];
    }
    const t27 = `/app/account/${accountId}/ai-training`;
    let t28;
    if ($[41] !== localizedPath || $[42] !== t27) {
      t28 = localizedPath(t27);
      $[41] = localizedPath;
      $[42] = t27;
      $[43] = t28;
    } else {
      t28 = $[43];
    }
    let t29;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <T>AI Training</T>;
      $[44] = t29;
    } else {
      t29 = $[44];
    }
    let t30;
    if ($[45] !== t28) {
      t30 = {
        href: t28,
        title: t29,
        badge: "BETA" as const,
        exactMatch: false
      };
      $[45] = t28;
      $[46] = t30;
    } else {
      t30 = $[46];
    }
    const t31 = `/app/account/${accountId}/analytics`;
    let t32;
    if ($[47] !== localizedPath || $[48] !== t31) {
      t32 = localizedPath(t31);
      $[47] = localizedPath;
      $[48] = t31;
      $[49] = t32;
    } else {
      t32 = $[49];
    }
    let t33;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <T>Analytics</T>;
      $[50] = t33;
    } else {
      t33 = $[50];
    }
    let t34;
    if ($[51] !== t32) {
      t34 = {
        href: t32,
        title: t33,
        exactMatch: false
      };
      $[51] = t32;
      $[52] = t34;
    } else {
      t34 = $[52];
    }
    let t35;
    if ($[53] !== t14 || $[54] !== t18 || $[55] !== t22 || $[56] !== t26 || $[57] !== t30 || $[58] !== t34) {
      t35 = [t14, t18, t22, t26, t30, t34];
      $[53] = t14;
      $[54] = t18;
      $[55] = t22;
      $[56] = t26;
      $[57] = t30;
      $[58] = t34;
      $[59] = t35;
    } else {
      t35 = $[59];
    }
    t10 = t35;
  }
  const growthChildren = t10;
  const hasActiveGrowthChild = isOnGrowthPage;
  if (!accountId) {
    return null;
  }
  let t11;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = className => <TrendingUpIcon ref={growthIconRef} size={20} className={className} />;
    $[60] = t11;
  } else {
    t11 = $[60];
  }
  let t12;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <T>Growth</T>;
    $[61] = t12;
  } else {
    t12 = $[61];
  }
  let t13;
  if ($[62] !== growthChildren) {
    t13 = growthChildren.map(_temp2);
    $[62] = growthChildren;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== hasActiveGrowthChild || $[65] !== isGrowthExpanded || $[66] !== t13) {
    t14 = <SidebarExpandableItem renderCustomIcon={t11} customIconRef={growthIconRef} label={t12} isExpanded={isGrowthExpanded} onToggle={handleGrowthToggle} hasActiveChild={hasActiveGrowthChild}>{t13}</SidebarExpandableItem>;
    $[64] = hasActiveGrowthChild;
    $[65] = isGrowthExpanded;
    $[66] = t13;
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  return t14;
});

// ============================================
// CONTENT NAV SECTION - Self-contained with pathname subscription
// ============================================

interface ContentNavSectionProps {
  localizedPath: (path: string) => string;
}

/**
 * ContentNavSection - Encapsulates all content-related navigation with its own pathname subscription
 * This isolates pathname changes to just this section, preventing full sidebar re-renders
 */
const ContentNavSection = memo(function ContentNavSection(t0) {
  const $ = _c(47);
  const {
    localizedPath
  } = t0;
  const pathname = usePathname();
  const calendarIconRef = useRef(null);
  let id;
  let t1;
  if ($[0] !== pathname) {
    id = pathname.split("/")[3];
    t1 = id && id.match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/);
    $[0] = pathname;
    $[1] = id;
    $[2] = t1;
  } else {
    id = $[1];
    t1 = $[2];
  }
  const isValidUUID = t1;
  const accountIdFromUrl = isValidUUID ? id : null;
  const [storedAccountId, setStoredAccountId] = useState(null);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const stored = getStoredSelectedAccountId();
      if (stored) {
        setStoredAccountId(stored);
      }
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const accountId = accountIdFromUrl || storedAccountId;
  let t4;
  if ($[5] !== pathname) {
    t4 = pathname?.includes("/ai-content") || pathname?.includes("/schedule") || pathname?.includes("/viral");
    $[5] = pathname;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const isOnContentPage = t4;
  const [isContentExpanded, setIsContentExpanded] = useState(isOnContentPage);
  let t5;
  if ($[7] !== isOnContentPage) {
    t5 = () => {
      if (!isOnContentPage) {
        const stored_0 = getStoredContentExpandedState();
        setIsContentExpanded(stored_0 ?? false);
      }
    };
    $[7] = isOnContentPage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] !== isContentExpanded || $[11] !== isOnContentPage) {
    t7 = () => {
      if (isOnContentPage && !isContentExpanded) {
        setIsContentExpanded(true);
      }
    };
    $[10] = isContentExpanded;
    $[11] = isOnContentPage;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== isOnContentPage) {
    t8 = [isOnContentPage];
    $[13] = isOnContentPage;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsContentExpanded(_temp3);
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleContentToggle = t9;
  let t10;
  bb0: {
    if (!accountId) {
      let t11;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = [];
        $[16] = t11;
      } else {
        t11 = $[16];
      }
      t10 = t11;
      break bb0;
    }
    const t11 = `/app/account/${accountId}/ai-content`;
    let t12;
    if ($[17] !== localizedPath || $[18] !== t11) {
      t12 = localizedPath(t11);
      $[17] = localizedPath;
      $[18] = t11;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    let t13;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <T>Generate</T>;
      $[20] = t13;
    } else {
      t13 = $[20];
    }
    let t14;
    if ($[21] !== t12) {
      t14 = {
        href: t12,
        title: t13,
        badge: "NEW" as const,
        exactMatch: false
      };
      $[21] = t12;
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    const t15 = `/app/account/${accountId}/schedule`;
    let t16;
    if ($[23] !== localizedPath || $[24] !== t15) {
      t16 = localizedPath(t15);
      $[23] = localizedPath;
      $[24] = t15;
      $[25] = t16;
    } else {
      t16 = $[25];
    }
    let t17;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <T>Schedule</T>;
      $[26] = t17;
    } else {
      t17 = $[26];
    }
    let t18;
    if ($[27] !== t16) {
      t18 = {
        href: t16,
        title: t17,
        exactMatch: false
      };
      $[27] = t16;
      $[28] = t18;
    } else {
      t18 = $[28];
    }
    const t19 = `/app/account/${accountId}/viral`;
    let t20;
    if ($[29] !== localizedPath || $[30] !== t19) {
      t20 = localizedPath(t19);
      $[29] = localizedPath;
      $[30] = t19;
      $[31] = t20;
    } else {
      t20 = $[31];
    }
    let t21;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <T>Viral Library</T>;
      $[32] = t21;
    } else {
      t21 = $[32];
    }
    let t22;
    if ($[33] !== t20) {
      t22 = {
        href: t20,
        title: t21,
        exactMatch: false
      };
      $[33] = t20;
      $[34] = t22;
    } else {
      t22 = $[34];
    }
    let t23;
    if ($[35] !== t14 || $[36] !== t18 || $[37] !== t22) {
      t23 = [t14, t18, t22];
      $[35] = t14;
      $[36] = t18;
      $[37] = t22;
      $[38] = t23;
    } else {
      t23 = $[38];
    }
    t10 = t23;
  }
  const contentChildren = t10;
  const hasActiveContentChild = isOnContentPage;
  if (!accountId) {
    return null;
  }
  let t11;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = className => <CalendarDaysIcon ref={calendarIconRef} size={20} className={className} />;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <T>Content</T>;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== contentChildren) {
    t13 = contentChildren.map(_temp4);
    $[41] = contentChildren;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== hasActiveContentChild || $[44] !== isContentExpanded || $[45] !== t13) {
    t14 = <SidebarExpandableItem renderCustomIcon={t11} customIconRef={calendarIconRef as React.RefObject<TrendingUpIconHandle | null>} label={t12} isExpanded={isContentExpanded} onToggle={handleContentToggle} hasActiveChild={hasActiveContentChild}>{t13}</SidebarExpandableItem>;
    $[43] = hasActiveContentChild;
    $[44] = isContentExpanded;
    $[45] = t13;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  return t14;
});

// ============================================
// MAIN SIDEBAR COMPONENT
// ============================================

import { useGetAccountById, useGetAllTargetsForAccount } from "@/utils/react-query-hooks";
import { useSetupChecklist } from "@/hooks/useSetupChecklist";
import { useConfettiContext } from "@/contexts/ConfettiContextProvider";
import { useDemoContext } from "@/contexts/DemoContext";
import { toast } from "sonner";

// ============================================
// MAIN SIDEBAR COMPONENT
// ============================================

interface AppSidebarProps {
  userProfile: Table<"user_profiles">;
  hasUpgradableAccounts: boolean;
}

/**
 * AppSidebar - Main sidebar navigation
 * Wrapped in memo to prevent re-renders from parent (ClientLayout)
 * Pathname subscriptions are isolated to child components (GrowthNavSection, SidebarLink)
 */
const AppSidebar = memo(function AppSidebar({
  userProfile,
  hasUpgradableAccounts
}: AppSidebarProps) {
  const user = useLoggedInUser();
  const localizedPath = useLocalizedAppPath();
  const pathname = usePathname();
  const upgradeIconRef = useRef<ZapIconHandle>(null);
  const tiktokIconRef = useRef<BadgeCheckIconHandle>(null);
  const [isUpgradeHovered, setIsUpgradeHovered] = useState(false);
  const isDemoUser = user && user.email === DEMO_USER_EMAIL;
  const {
    isDemoMode
  } = useDemoContext();
  const {
    setIsConnectAccountModalOpened,
    setIsUpgradeModalOpened,
    openUpgradeModal
  } = useModalContext();

  // Derive account ID from URL first, fall back to localStorage
  // This ensures checklist updates when navigating between accounts
  // URL-derived ID is SSR-safe; localStorage deferred to useEffect to prevent hydration mismatch
  const accountIdFromUrl = useMemo(() => {
    const match = pathname?.match(/\/app\/account\/([a-f0-9-]+)/i);
    return match ? match[1] : null;
  }, [pathname]);
  const [storedAccountId, setStoredAccountId] = useState<string | null>(null);
  useEffect(() => {
    if (!accountIdFromUrl) {
      setStoredAccountId(getStoredSelectedAccountId());
    }
  }, [accountIdFromUrl]);
  const activeAccountId = accountIdFromUrl || storedAccountId;

  // Fetch account data
  const {
    data: account
  } = useGetAccountById(activeAccountId || "");
  const {
    data: targets
  } = useGetAllTargetsForAccount(activeAccountId);
  const queryClient = useQueryClient();
  const {
    setShowConfetti
  } = useConfettiContext();

  // In-flight guard so rapid double-clicks can't fire concurrent start-growth requests
  const isStartingGrowthRef = useRef(false);

  // Handle Start Growth click - opens upgrade modal for trial, starts growth for paid
  const handleStartGrowth = useCallback(async () => {
    if (!activeAccountId) return;
    if (account?.account_type === "trial") {
      // Trial account → open upgrade modal
      openUpgradeModal(activeAccountId);
    } else {
      // Paid account → start growth + trigger immediate activity
      // Re-entrancy guard: ignore clicks while a request is already in flight
      if (isStartingGrowthRef.current) return;
      isStartingGrowthRef.current = true;
      try {
        const res = await fetch("/api/account/start-growth", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            accountId: activeAccountId
          })
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body && typeof body.error === "string" && body.error || "Failed to start growth. Please try again.");
        }
        toast.success("Growth started successfully!");
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        // Server already activated the account — just refresh the relevant queries
        // (no redundant mutateAccount({ active: true }) that would re-PATCH the server).
        queryClient.invalidateQueries(["account", activeAccountId]);
        queryClient.invalidateQueries(["activities", activeAccountId]);
      } catch (error) {
        console.error("Start growth error:", error);
        toast.error(error instanceof Error ? error.message : "Failed to start growth. Please try again.");
      } finally {
        isStartingGrowthRef.current = false;
      }
    }
  }, [activeAccountId, account?.account_type, openUpgradeModal, queryClient, setShowConfetti]);
  const checklistData = useMemo(() => {
    if (!account) return undefined;
    return {
      ...account,
      targets: targets || [],
      is_running: account.active,
      has_subscription: account.account_type !== "trial"
    };
  }, [account, targets]);
  const setupChecklistItems = useSetupChecklist(activeAccountId, checklistData, {
    onStartGrowth: handleStartGrowth
  });
  return <nav className={classNames("flex h-full w-52 min-w-52 flex-col overflow-y-auto rounded-l-2xl bg-sidebar-bg px-3 py-4 lg:w-60 lg:min-w-60", isDemoMode && "pb-[70px]")}>
            {/* Outfame Logo */}
            <Link href="/app" className="group mb-4 flex items-center px-2.5">
                <Image width={140} height={28} className="h-7 w-auto transition-all duration-300 ease-in-out group-hover:opacity-80" src="/img/outfame-logo-full.svg" alt="Outfame" loading="eager" />
            </Link>

            {/* Account Selector */}
            <div className="mb-2 border-b border-black/5 pb-2.5">
                <SidebarAccountSelector />
            </div>

            {/* Main Navigation */}
            <div className="grid flex-1 content-start gap-2">
                {/* Expandable Growth Item - self-contained with its own pathname subscription */}
                <GrowthNavSection localizedPath={localizedPath} />

                {/* Expandable Content Item - self-contained with its own pathname subscription */}
                <ContentNavSection localizedPath={localizedPath} />
            </div>

            {/* Bottom Section - hide border for demo users since most items are hidden */}
            <div className={classNames("mt-auto grid gap-2 pt-4", !isDemoUser && "border-t border-black/5")}>
                {/* Setup Checklist - hide for demo users, when growth is already running, or when no items */}
                {!isDemoUser && !account?.active && setupChecklistItems.length > 0 && <div className="mb-3">
                        <SetupChecklist items={setupChecklistItems} onConnectAccount={() => setIsConnectAccountModalOpened(true)} onUpgrade={() => openUpgradeModal(activeAccountId || undefined)} />
                    </div>}

                {/* Upgrade button */}
                {!isDemoUser && hasUpgradableAccounts && <button type="button" onClick={() => openUpgradeModal(activeAccountId || undefined)} onMouseEnter={() => {
        setIsUpgradeHovered(true);
        upgradeIconRef.current?.startAnimation();
      }} onMouseLeave={() => {
        setIsUpgradeHovered(false);
        upgradeIconRef.current?.stopAnimation();
      }} className={classNames("group relative flex items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-base font-semibold", "w-full text-left transition-all duration-150 ease-out")} style={{
        backgroundColor: isUpgradeHovered ? "rgba(255,255,255,0.5)" : "transparent",
        color: isUpgradeHovered ? "#c2410c" : "#ea580c"
      }}>
                        <ZapIcon ref={upgradeIconRef} size={20} className="text-orange-500" />
                        <span>
                            <T>Upgrade</T>
                        </span>
                    </button>}

                {/* Connect Account */}
                {!isDemoUser && <SidebarLink icon={<Plus />} animation="rotate" onClick={() => setIsConnectAccountModalOpened(true)}>
                        <T>Connect Account</T>
                    </SidebarLink>}

                {/* Buy TikTok Accounts — marketplace cross-sell (relocated from main nav) */}
                <SidebarLink href={localizedPath("/app/tiktok-accounts")} renderCustomIcon={className => <BadgeCheckIcon ref={tiktokIconRef} size={20} className={className} />} customIconRef={tiktokIconRef}>
                    <T>Buy TikTok Accounts</T>
                </SidebarLink>

                {/* Referrals */}
                <SidebarLink href={localizedPath("/app/referrals")} icon={<Users />} animation="bounce">
                    <T>Referrals</T>
                </SidebarLink>

                {/* Help Center */}
                <SidebarLink icon={<HelpCircle />} onClick={() => window.open("https://support.outfame.com/en/", "_blank", "noopener,noreferrer")}>
                    Help Center
                </SidebarLink>

                {/* Call Support — only shown when phone line is active */}
                {BRAND.supportPhone && <SidebarLink icon={<Phone />} onClick={callSupport}>
                        Call Support
                    </SidebarLink>}
            </div>
        </nav>;
});
export default AppSidebar;
function _temp(prev) {
  const newValue = !prev;
  setStoredExpandedState(newValue);
  return newValue;
}
function _temp2(child) {
  return <SidebarChildLink key={child.href} href={child.href} exactMatch={child.exactMatch} badge={child.badge ? <MenuBadge type={child.badge} /> : undefined}>{child.title}</SidebarChildLink>;
}
function _temp3(prev) {
  const newValue = !prev;
  setStoredContentExpandedState(newValue);
  return newValue;
}
function _temp4(child) {
  return <SidebarChildLink key={child.href} href={child.href} exactMatch={child.exactMatch} badge={child.badge ? <MenuBadge type={child.badge} /> : undefined}>{child.title}</SidebarChildLink>;
}